/**
 * Bridge between the v2 class Overview tab and the still-legacy single-student page.
 *
 * The Overview is Flux-free (its subject scope lives in React state via useClassOverview), but the
 * single-student page reads its subject filter from the legacy TeacherClassroomScope store, which
 * defaults to the first subject. Without this, clicking a student off the Overview drops you onto
 * that page scoped to the first subject instead of the subject you were looking at.
 *
 * The Overview records its current subject selection here; TeacherClassroomScope.store reads it to
 * choose the subject dropdown's active option. Session-scoped (a plain in-memory map) — it carries
 * the selection across SPA navigation and resets on a full reload, which matches the legacy filter.
 */
const overviewSubjectScopeByClassroom = new Map<string, string[]>();

export interface OverviewDateScope {
  /** A RANGES id — a preset name (e.g. 'Last month') or 'Custom'. */
  dateMode: string;
  startDate: number;
  endDate: number;
}

const overviewDateScopeByClassroom = new Map<string, OverviewDateScope>();

/**
 * The class's current date scope — the latest resolved range set on EITHER surface (the v2
 * Overview or the single-student page); both record here and re-seed from here, so the range
 * stays in lockstep across navigation with last-change-wins semantics.
 */
export const setOverviewDateScope = (classroomId: string, scope: OverviewDateScope): void => {
  const existing = overviewDateScopeByClassroom.get(classroomId);
  if (
    existing &&
    existing.dateMode === scope.dateMode &&
    existing.startDate === scope.startDate &&
    existing.endDate === scope.endDate
  ) {
    return;
  }
  overviewDateScopeByClassroom.set(classroomId, scope);
};

/**
 * The date range the single-student page should inherit for this classroom, or `null` when the
 * Overview hasn't recorded one (caller keeps its own default).
 */
export const getOverviewDateScope = (classroomId: string): OverviewDateScope | null =>
  overviewDateScopeByClassroom.get(classroomId) ?? null;

export interface OverviewAttemptScope {
  /** null = "Most Recent" (the backend `most_recent` flag); a number selects that attempt. */
  attemptNumber: number | null;
}

const overviewAttemptScopeByClassroom = new Map<string, OverviewAttemptScope>();

/**
 * The class's current attempt-number scope — same last-change-wins contract as the date scope.
 * Re-recording an unchanged value keeps the existing entry, so adopt-once consumers stay settled.
 */
export const setOverviewAttemptScope = (
  classroomId: string,
  attemptNumber: number | null
): void => {
  const existing = overviewAttemptScopeByClassroom.get(classroomId);
  if (existing && existing.attemptNumber === attemptNumber) {
    return;
  }
  overviewAttemptScopeByClassroom.set(classroomId, {attemptNumber});
};

/**
 * The attempt scope the Overview or a student page last recorded for this classroom, or `null`
 * when none has been recorded (caller keeps its own default).
 */
export const getOverviewAttemptScope = (classroomId: string): OverviewAttemptScope | null =>
  overviewAttemptScopeByClassroom.get(classroomId) ?? null;

/** v2 subject scope: `[]` means "All Subjects", `[id]` a single subject (mirrors OverviewScope). */
export const setOverviewSubjectScope = (classroomId: string, subjectIds: string[]): void => {
  overviewSubjectScopeByClassroom.set(classroomId, subjectIds);
};

/**
 * The Overview's recorded subject selection for this classroom, or `null` when none has been
 * recorded (caller keeps its own default). Lets a remounting Overview re-seed its own selection.
 */
export const getOverviewSubjectScope = (classroomId: string): string[] | null =>
  overviewSubjectScopeByClassroom.get(classroomId) ?? null;

/**
 * The legacy subject-dropdown option id the single-student page should open on for this classroom,
 * or `null` when the Overview hasn't recorded a selection (caller keeps its own default).
 */
export const getOverviewActiveSubjectOptionId = (classroomId: string): string | null => {
  const subjectIds = overviewSubjectScopeByClassroom.get(classroomId);
  if (!subjectIds) {
    return null;
  }
  return subjectIds.length === 0 ? 'all-subjects' : subjectIds[0];
};
